import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import CachedBackend from "./CachedBackend.js";
// Available languages with locale codes
export const AVAILABLE_LANGUAGES = {
    "en-US": {
        code: "en-US",
        name: "English",
        nativeName: "English",
        flag: "🇺🇸",
        isRTL: false,
        shortName: "EN",
    },
    "tr-TR": {
        code: "tr-TR",
        name: "Turkish",
        nativeName: "Türkçe",
        flag: "🇹🇷",
        isRTL: false,
        shortName: "TR",
    },
};
// ,
//   'es-ES': {
//     code: 'es-ES',
//     name: 'Spanish',
//     nativeName: 'Español',
//     flag: '🇪🇸',
//     isRTL: false ,
//     shortName: 'ES'
//   },
//   'fr-FR': {
//     code: 'fr-FR',
//     name: 'French',
//     nativeName: 'Français',
//     flag: '🇫🇷',
//     isRTL: false ,
//     shortName: 'FR'
//   },
//   'de-DE': {
//     code: 'de-DE',
//     name: 'German',
//     nativeName: 'Deutsch',
//     flag: '🇩🇪',
//     isRTL: false ,
//     shortName: 'DE'
//   },
//   'it-IT': {
//     code: 'it-IT',
//     name: 'Italian',
//     nativeName: 'Italiano',
//     flag: '🇮🇹',
//     isRTL: false ,
//     shortName: 'IT'
//   },
//   'pt-PT': {
//     code: 'pt-PT',
//     name: 'Portuguese',
//     nativeName: 'Português',
//     flag: '🇵🇹',
//     isRTL: false ,
//     shortName: 'PT'
//   },
//   'ja-JP': {
//     code: 'ja-JP',
//     name: 'Japanese',
//     nativeName: '日本語',
//     flag: '🇯🇵',
//     isRTL: false ,
//     shortName: 'JA'
//   },
//   'ko-KR': {
//     code: 'ko-KR',
//     name: 'Korean',
//     nativeName: '한국어',
//     flag: '🇰🇷',
//     isRTL: false ,
//     shortName: 'KO'
//   },
//   'zh-CN': {
//     code: 'zh-CN',
//     name: 'Chinese',
//     nativeName: '中文',
//     flag: '🇨🇳',
//     isRTL: false ,
//     shortName: 'ZH'
//   },
//   'ar-SA': {
//     code: 'ar-SA',
//     name: 'Arabic',
//     nativeName: 'العربية',
//     flag: '🇸🇦',
//     isRTL: true ,
//     shortName: 'AR'
//   },
// Helper function to get short code from full locale
export const getShortLanguageCode = (locale) => {
    const lang = AVAILABLE_LANGUAGES[locale];
    const [shortCode] = locale.split("-");
    return lang ? (shortCode ?? "en") : (shortCode ?? "en");
};
// Routers mount every route under both the full locale (en-US) and its short code (en)
const isLanguageSegment = (segment) => segment !== undefined &&
    Object.keys(AVAILABLE_LANGUAGES).some((locale) => locale === segment || getShortLanguageCode(locale) === segment);
export const stripLanguagePrefix = (pathname) => {
    const [firstSegment, ...rest] = pathname.split("/").filter(Boolean);
    if (!isLanguageSegment(firstSegment))
        return pathname;
    return rest.length > 0 ? `/${rest.join("/")}` : "/";
};
export const withLanguagePrefix = (path, pathname) => {
    if (!path.startsWith("/") || path.startsWith("//"))
        return path;
    if (isLanguageSegment(path.split("/")[1]))
        return path;
    const language = pathname.split("/")[1];
    if (!isLanguageSegment(language))
        return path;
    return path === "/" ? `/${language}` : `/${language}${path}`;
};
// Helper function to get full locale from short code
export const getFullLocaleCode = (codeInput) => {
    // If it's already a valid full locale code, return it
    if (AVAILABLE_LANGUAGES[codeInput]) {
        return codeInput;
    }
    // Otherwise, treat it as a short code and find the corresponding full locale
    const entry = Object.values(AVAILABLE_LANGUAGES).find((lang) => lang.code.startsWith(`${codeInput}-`));
    return entry?.code || "en-US";
};
// Helper function to get language direction
export const getLanguageDirection = (locale) => {
    const lang = AVAILABLE_LANGUAGES[locale];
    return lang?.isRTL ? "rtl" : "ltr";
};
// Default configuration
export const DEFAULT_CONFIG = {
    defaultLanguage: "en-US",
    supportedLanguages: Object.values(AVAILABLE_LANGUAGES),
    fallbackLanguage: "en-US",
    debug: false,
    detection: {
        order: ["path", "navigator"],
        caches: [],
        lookupFromPathIndex: 0,
        lookupFromSubdomainIndex: 0,
    },
    backend: {
        loadPath: "/locales/{{lng}}/{{ns}}.json",
    },
    interpolation: {
        escapeValue: false,
    },
};
export const initializeI18n = (config = {}) => {
    const finalConfig = {
        ...DEFAULT_CONFIG,
        ...config,
        detection: {
            ...DEFAULT_CONFIG.detection,
            ...(config.detection ?? {}),
        },
        backend: {
            ...DEFAULT_CONFIG.backend,
            ...(config.backend ?? {}),
        },
        interpolation: {
            ...DEFAULT_CONFIG.interpolation,
            ...(config.interpolation ?? {}),
        },
    };
    void i18n
        .use(CachedBackend)
        .use(LanguageDetector)
        .use(initReactI18next)
        .init({
        returnObjects: true,
        fallbackLng: finalConfig.fallbackLanguage,
        debug: finalConfig.debug,
        // Configure language detection with your priority order
        detection: {
            order: finalConfig.detection.order,
            caches: finalConfig.detection.caches,
            lookupFromPathIndex: 0,
        },
        // Backend configuration for loading translation files
        backend: {
            loadPath: (lngs, _namespaces) => {
                const language = lngs[0] ?? finalConfig.defaultLanguage;
                const [shortCode] = language.split("-");
                return finalConfig.backend.loadPath.replace("{{lng}}", shortCode ?? "en");
            },
        },
        // Whitelist supported languages
        supportedLngs: [
            ...finalConfig.supportedLanguages.map((lang) => lang.code),
            ...finalConfig.supportedLanguages.map((lang) => getShortLanguageCode(lang.code)),
        ],
        // Namespaces
        defaultNS: "landing",
        ns: ["landing", "app_terms", "app_content", "blog", "docs"],
        interpolation: finalConfig.interpolation,
        // React-specific options
        react: {
            bindI18n: "languageChanged",
            bindI18nStore: "",
            transEmptyNodeValue: "",
            transSupportBasicHtmlNodes: true,
            transKeepBasicHtmlNodesFor: ["br", "strong", "i", "em", "span"],
            useSuspense: false,
        },
        // Load missing translation handler
        saveMissing: finalConfig.debug,
        missingKeyHandler: (lng, ns, key, _fallbackValue) => {
            if (finalConfig.debug) {
                console.warn(`Missing translation: ${lng.join(",")}:${ns}:${key}`);
            }
        },
    });
    // Set HTML lang attribute and direction when language changes
    i18n.on("languageChanged", (lng) => {
        const fullLocale = getFullLocaleCode(lng);
        const shortCode = getShortLanguageCode(fullLocale);
        // Update HTML attributes
        if (typeof document !== "undefined") {
            document.documentElement.lang = fullLocale;
            document.documentElement.setAttribute("data-language", shortCode);
            document.documentElement.setAttribute("data-locale", fullLocale);
            document.documentElement.dir = getLanguageDirection(fullLocale);
        }
    });
    return i18n;
};
// Safe language change function to prevent loops
export const safeChangeLanguage = async (language) => {
    try {
        await i18n.changeLanguage(language);
    }
    catch (error) {
        console.error("Error changing language:", error);
    }
};
export { i18n };
export default i18n;
//# sourceMappingURL=config.js.map